import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import ABCDInput from "../../../components/inputs/ABCDInput";

const pick = (items) => items[Math.floor(Math.random() * items.length)];
const choiceLabels = ["A", "B", "C", "D"];

function shuffle(items) {
  const result = [...items];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const target = Math.floor(Math.random() * (index + 1));
    [result[index], result[target]] = [result[target], result[index]];
  }
  return result;
}

function withChoices(data, choices, correctLatex) {
  const options = shuffle(choices);
  return { data: { ...data, options, correctIndex: options.indexOf(correctLatex) } };
}

function ChoiceList({ options }) {
  return (
    <div style={{ display: "grid", gap: 8, margin: "14px 0" }}>
      {options.map((option, index) => (
        <div key={option} style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
          <strong>{choiceLabels[index]}.</strong>
          <InlineMath math={option} />
        </div>
      ))}
    </div>
  );
}

const taskRegistry = {
  DivisibilityRemainderFormTask: {
    generate() {
      const modulus = pick([3, 4, 5, 6, 7]);
      const remainder = 1 + Math.floor(Math.random() * (modulus - 1));
      const correct = String.raw`n=${modulus}k+${remainder},\quad k\in\mathbb{Z}`;
      return withChoices({ modulus, remainder }, [
        correct,
        String.raw`n=${remainder}k+${modulus},\quad k\in\mathbb{Z}`,
        String.raw`n=${modulus}+${remainder}k,\quad k\in\mathbb{Z}`,
        String.raw`n=${modulus}k-${remainder},\quad k\in\mathbb{Z}`,
      ], correct);
    },
    renderTask({ data }) {
      return (
        <div style={{ textAlign: "left", lineHeight: 1.7 }}>
          <p>
            Liczba całkowita <InlineMath math="n" /> przy dzieleniu przez {data.modulus}
            daje resztę {data.remainder}. Wskaż zapis opisujący wszystkie takie liczby.
          </p>
          <ChoiceList options={data.options} />
        </div>
      );
    },
    renderExplanation({ data }) {
      return (
        <div style={{ textAlign: "left", lineHeight: 1.7 }}>
          <p>Każda taka liczba składa się z wielokrotności dzielnika i podanej reszty.</p>
          <BlockMath math={String.raw`n=${data.modulus}k+${data.remainder},\quad k\in\mathbb{Z}`} />
          <p>Reszta spełnia warunek <InlineMath math={String.raw`0\le r<${data.modulus}`} />.</p>
        </div>
      );
    },
  },

  IndependentRemaindersTask: {
    generate() {
      const modulus = pick([3, 4, 5, 6]);
      const firstRemainder = 1;
      const secondRemainder = pick(Array.from({ length: modulus - 2 }, (_, index) => index + 2));
      const correct = String.raw`a=${modulus}x+${firstRemainder},\quad b=${modulus}y+${secondRemainder},\quad x,y\in\mathbb{Z}`;
      return withChoices({ modulus, firstRemainder, secondRemainder }, [
        correct,
        String.raw`a=${modulus}x+${firstRemainder},\quad b=${modulus}x+${secondRemainder},\quad x\in\mathbb{Z}`,
        String.raw`a=${firstRemainder}x+${modulus},\quad b=${secondRemainder}y+${modulus},\quad x,y\in\mathbb{Z}`,
        String.raw`a=${modulus}x-${firstRemainder},\quad b=${modulus}y-${secondRemainder},\quad x,y\in\mathbb{Z}`,
      ], correct);
    },
    renderTask({ data }) {
      return (
        <div style={{ textAlign: "left", lineHeight: 1.7 }}>
          <p>
            Liczba <InlineMath math="a" /> daje przy dzieleniu przez {data.modulus} resztę {data.firstRemainder},
            a niezależnie wybrana liczba <InlineMath math="b" /> daje resztę {data.secondRemainder}.
            Wskaż poprawny zapis obu liczb.
          </p>
          <ChoiceList options={data.options} />
        </div>
      );
    },
    renderExplanation({ data }) {
      return (
        <div style={{ textAlign: "left", lineHeight: 1.7 }}>
          <BlockMath math={String.raw`a=${data.modulus}x+${data.firstRemainder},\qquad b=${data.modulus}y+${data.secondRemainder},\qquad x,y\in\mathbb{Z}`} />
          <p>
            Używamy dwóch parametrów, ponieważ liczby są wybierane niezależnie. Ten sam parametr
            narzucałby dodatkową, niepodaną w zadaniu zależność między <InlineMath math="a" /> i <InlineMath math="b" />.
          </p>
        </div>
      );
    },
  },

  ComplementaryRemaindersProofTask: {
    generate() {
      const modulus = pick([3, 4, 5, 6, 7]);
      const remainder = 1 + Math.floor(Math.random() * (modulus - 1));
      const complement = modulus - remainder;
      const correct = `a+b=${modulus}(x+y+1)`;
      return withChoices({ modulus, remainder, complement }, [
        correct,
        `a+b=${modulus}(x+y)+1`,
        `a+b=${modulus}(x+y)+${modulus + 1}`,
        `a+b=${modulus}(xy+1)`,
      ], correct);
    },
    renderTask({ data }) {
      return (
        <div style={{ textAlign: "left", lineHeight: 1.7 }}>
          <p>Dane są liczby całkowite:</p>
          <BlockMath math={String.raw`a=${data.modulus}x+${data.remainder},\qquad b=${data.modulus}y+${data.complement},\qquad x,y\in\mathbb{Z}`} />
          <p>Wskaż przekształcenie, które dowodzi, że suma <InlineMath math="a+b" /> jest podzielna przez {data.modulus}.</p>
          <ChoiceList options={data.options} />
        </div>
      );
    },
    renderExplanation({ data }) {
      return (
        <div style={{ textAlign: "left", lineHeight: 1.7 }}>
          <BlockMath math={String.raw`a+b=(${data.modulus}x+${data.remainder})+(${data.modulus}y+${data.complement})=${data.modulus}x+${data.modulus}y+${data.modulus}=${data.modulus}(x+y+1)`} />
          <p>
            Ponieważ <InlineMath math="x+y+1\in\mathbb{Z}" />, suma ma postać
            <InlineMath math={String.raw` ${data.modulus}\cdot q`} /> dla pewnego <InlineMath math="q\in\mathbb{Z}" />.
          </p>
        </div>
      );
    },
  },

  ConsecutiveProductProofTask: {
    generate() {
      const mode = pick(["two", "three"]);
      if (mode === "two") {
        const correct = String.raw`n(n+1)\text{ jest parzyste, bo jedna z dwóch kolejnych liczb jest parzysta}`;
        return withChoices({ mode, divisor: 2 }, [
          correct,
          String.raw`n(n+1)\text{ jest parzyste, bo obie kolejne liczby są parzyste}`,
          "n(n+1)=2n+1",
          String.raw`n(n+1)\text{ jest zawsze podzielne przez }4`,
        ], correct);
      }
      const correct = String.raw`n^3-n=n(n-1)(n+1)\text{ i jedna z trzech kolejnych liczb jest podzielna przez }3`;
      return withChoices({ mode, divisor: 3 }, [
        correct,
        String.raw`n^3-n=n(n-1)\text{ i każda liczba }n\text{ jest podzielna przez }3`,
        "n^3-n=(n-1)^3",
        String.raw`n^3-n\text{ jest podzielne przez }3\text{ tylko dla }n=3`,
      ], correct);
    },
    renderTask({ data }) {
      const expression = data.mode === "two" ? "n(n+1)" : "n^3-n";
      return (
        <div style={{ textAlign: "left", lineHeight: 1.7 }}>
          <p>
            Dla dowolnej liczby całkowitej <InlineMath math="n" /> uzasadnij podzielność wyrażenia
            <InlineMath math={` ${expression}`} /> przez {data.divisor}. Wskaż poprawne uzasadnienie.
          </p>
          <ChoiceList options={data.options} />
        </div>
      );
    },
    renderExplanation({ data }) {
      if (data.mode === "two") {
        return (
          <div style={{ textAlign: "left", lineHeight: 1.7 }}>
            <p>
              Spośród dwóch kolejnych liczb <InlineMath math="n" /> i <InlineMath math="n+1" />
              dokładnie jedna jest parzysta. Ich iloczyn zawiera więc czynnik <InlineMath math="2" />.
            </p>
          </div>
        );
      }
      return (
        <div style={{ textAlign: "left", lineHeight: 1.7 }}>
          <BlockMath math="n^3-n=n(n^2-1)=n(n-1)(n+1)" />
          <p>
            Otrzymaliśmy iloczyn trzech kolejnych liczb całkowitych. Jedna z nich jest podzielna przez
            <InlineMath math=" 3" />, więc cały iloczyn również jest podzielny przez <InlineMath math="3" />.
          </p>
        </div>
      );
    },
  },
};

function moduleFor(task) {
  const module = taskRegistry[task?.type];
  if (!module) throw new Error(`Nieznany typ zadania (dowody podzielności): ${task?.type}`);
  return module;
}

export function generateTask(typeOrOptions) {
  const taskType = typeof typeOrOptions === "string"
    ? typeOrOptions
    : typeOrOptions?.type || pick(Object.keys(taskRegistry));
  return { type: taskType, ...taskRegistry[taskType].generate() };
}

export function generateTasks() {
  return Object.keys(taskRegistry).map((type) => generateTask(type));
}

export function renderTask(task) { return moduleFor(task).renderTask(task); }

export function renderInput(task, userAnswer, onChange, disabled) {
  return (
    <ABCDInput
      options={task.data.options.map((_, index) => ({ value: index, label: choiceLabels[index], textNode: choiceLabels[index] }))}
      value={Number.isInteger(userAnswer?.choiceIndex) ? userAnswer.choiceIndex : null}
      onChange={(choiceIndex) => onChange({ choiceIndex })}
      disabled={disabled}
    />
  );
}

export function checkAnswer(task, userAnswer) { return userAnswer?.choiceIndex === task.data.correctIndex; }
export function renderAnswer(task) { return <InlineMath math={task.data.options[task.data.correctIndex]} />; }
export function renderUserAnswer(task, userAnswer) {
  const option = task?.data?.options?.[userAnswer?.choiceIndex];
  return option ? <InlineMath math={option} /> : <i>(brak odpowiedzi)</i>;
}
export function renderExplanation(task) { return moduleFor(task).renderExplanation(task); }

export const registry = Object.fromEntries(Object.keys(taskRegistry).map((type) => [type, {
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderUserAnswer,
  renderExplanation,
}]));
